import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Edit2, Save, X, ChevronDown, ChevronRight } from "lucide-react";
import { ValidationRuleDto } from "@/api/Validations";
import { Account } from "@/types/Account";
import { AccountSelector } from "./AccountSelector";
import { JsonFieldsEditor } from "./JsonFieldsEditor";
import { Input } from "@/components/ui/input";

interface ValidationRuleCardProps {
  rule: ValidationRuleDto;
  accounts: Account[];
  accountsLoading: boolean;
  isEditing: boolean;
  isDraft: boolean;
  isSaving: boolean;
  supportedRuleTypes: string[];
  isExpanded: boolean;
  onToggleExpansion: () => void;
  onStartEditing: (ruleId: number) => void;
  onSaveRule: (ruleId: number) => void;
  onCancelEditing: (ruleId: number) => void;
  onUpdateRule: (ruleId: number, updates: Partial<ValidationRuleDto>) => void;
}

// Performance and data quality logging
const logComponentComplexity = (ruleId: number, propsCount: number, isEditing: boolean, isExpanded: boolean) => {
  console.log('[VALIDATION_RULE_CARD] Component complexity:', {
    ruleId,
    propsCount,
    isEditing,
    isExpanded,
    timestamp: new Date().toISOString()
  });
};

// Check if accounts string represents a comparison rule (has "a" and/or "b" properties)
const isComparisonRule = (accountsString: string | null | undefined): boolean => {
  if (!accountsString) return false;
  
  // Handle empty brackets or empty strings
  const trimmed = accountsString.trim();
  if (trimmed === '' || trimmed === '{}' || trimmed === '[]') return false;
  
  try {
    const parsed = JSON.parse(accountsString);
    
    let hasComparisonStructure = false;
    
    // Handle direct object format: {"a": [...], "b": [...]}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && ('a' in parsed || 'b' in parsed)) {
      hasComparisonStructure = true;
    }
    
    // Handle array format: [{"a": "...", "b": "..."}]
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && ('a' in parsed[0] || 'b' in parsed[0])) {
      hasComparisonStructure = true;
    }
    
    // Debug logging to track what's happening after save
    console.log('[COMPARISON_RULE_DETECTION_CARD]', {
      accountsString,
      parsed,
      isArray: Array.isArray(parsed),
      hasComparisonStructure,
      timestamp: new Date().toISOString()
    });
    
    return hasComparisonStructure;
  } catch (error) {
    console.warn('[COMPARISON_RULE_DETECTION_CARD] Parse failed:', accountsString, error);
    return false;
  }
};

// Parse comparison rule accounts (with "a" and "b" sets)
const parseComparisonAccounts = (accountsString: string | null | undefined): { a: string[], b: string[] } => {
  if (!accountsString) return { a: [], b: [] };
  
  try {
    const parsed = JSON.parse(accountsString);
    
    // Handle direct object format: {"a": [...], "b": [...]}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const aAccounts = Array.isArray(parsed.a) ? parsed.a : (parsed.a ? [parsed.a] : []);
      const bAccounts = Array.isArray(parsed.b) ? parsed.b : (parsed.b ? [parsed.b] : []);
      return { a: aAccounts, b: bAccounts };
    }
    
    // Handle array format: [{"a": "...", "b": "..."}]
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
      const obj = parsed[0];
      const aAccounts = Array.isArray(obj.a) ? obj.a : (obj.a ? [obj.a] : []);
      const bAccounts = Array.isArray(obj.b) ? obj.b : (obj.b ? [obj.b] : []);
      return { a: aAccounts, b: bAccounts };
    }
  } catch (error) {
    console.warn('Failed to parse comparison accounts:', accountsString, error);
  }
  
  return { a: [], b: [] };
};

// Get selected accounts for comparison rules
const getComparisonSelectedAccounts = (accountIds: string[], accounts: Account[]): Account[] => {
  return accounts.filter(account => 
    accountIds.includes(account.accountId.toString()) || 
    accountIds.includes(account.accountKey)
  );
};

const parseAccountIds = (accountsString: string | null | undefined): string[] => {
  if (!accountsString) return [];
  
  let cleanString = accountsString.trim();
  
  // Handle empty brackets "{}" 
  if (cleanString === '' || cleanString === '{}') {
    return [];
  }
  
  // Handle corrupted nested brackets like "[[[[[[[object Object],20101002,10030002]]]]]]"
  // Remove all brackets and extract content
  cleanString = cleanString.replace(/\[+/g, '').replace(/\]+/g, '');
  
  // Split by comma and clean up each ID
  const accountIds = cleanString.split(',').map(id => id.trim()).filter(id => {
    // Filter out empty strings, {}, and [object Object]
    return id && 
           id !== '{}' && 
           id !== '[object Object]' && 
           !id.includes('object Object') &&
           !id.startsWith('[') &&
           !id.endsWith(']');
  });
  
  return accountIds;
};

const getSelectedAccounts = (accountsString: string | null | undefined, accounts: Account[]): Account[] => {
  const selectedIds = parseAccountIds(accountsString);
  return accounts.filter(account => 
    selectedIds.includes(account.accountId.toString()) || 
    selectedIds.includes(account.accountKey)
  );
};

export const ValidationRuleCard: React.FC<ValidationRuleCardProps> = ({
  rule,
  accounts,
  accountsLoading,
  isEditing,
  isDraft,
  isSaving,
  supportedRuleTypes,
  isExpanded,
  onToggleExpansion,
  onStartEditing,
  onSaveRule,
  onCancelEditing,
  onUpdateRule
}) => {
  const ruleId = rule.validationRuleId!;
  
  // Log component complexity and render frequency
  const propsCount = 12; // Fixed count of props for this component
  React.useEffect(() => {
    logComponentComplexity(ruleId, propsCount, isEditing, isExpanded);
  }, [ruleId, isEditing, isExpanded, rule, accounts, isSaving]);

  const renderCollapsedHeader = () => (
    <div 
      className="flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors p-4 -m-4"
      onClick={onToggleExpansion}
    >
      <div className="flex items-center gap-3 flex-1">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        <div className="flex items-center gap-3">
          <div>
            <div className="font-medium">
              {rule.ruleName || (isDraft ? 'New Rule (Draft)' : 'Unnamed Rule')}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
              <span>{rule.ruleType === "unexplained-cash-movement" ? "Unexplained Cash Movement" : (rule.ruleType || 'No type specified')}</span>
              {rule.ruleType === "unexplained-cash-movement" && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  High Risk
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {isDraft && (
          <Badge variant="outline" className="text-blue-600 border-blue-200">
            Draft
          </Badge>
        )}
        {rule.disabled && !isDraft && (
          <Badge variant="outline" className="text-orange-600 border-orange-200">
            Disabled
          </Badge>
        )}
        {!rule.disabled && !isDraft && (
          <Badge variant="outline" className="text-green-600 border-green-200">
            Active
          </Badge>
        )}
        {rule.scope && (
          <Badge variant="secondary">{rule.scope}</Badge>
        )}
      </div>
    </div>
  );

  const renderExpandedHeader = () => (
    <div 
      className={`flex items-center justify-between transition-colors p-2 -m-2 mb-4 ${
        isEditing ? 'cursor-default' : 'cursor-pointer hover:bg-muted/30'
      }`}
      onClick={isEditing ? undefined : onToggleExpansion}
    >
      <div className="flex items-center gap-2">
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {isEditing ? 'Editing mode - complete or cancel to collapse' : 'Click to collapse'}
        </span>
      </div>
      {isEditing && (
        <Badge variant="secondary" className="text-primary border-primary">
          Editing
        </Badge>
      )}
    </div>
  );

  return (
    <div
      className={`p-6 border rounded-lg transition-all duration-200 ${
        isEditing 
          ? "border-primary bg-primary/5 shadow-md" 
          : "border-border hover:bg-muted/30 hover:shadow-sm"
      }`}
    >
      {!isExpanded ? (
        renderCollapsedHeader()
      ) : (
        <>
          {renderExpandedHeader()}
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 space-y-6">
              {/* Draft Indicator */}
              {isDraft && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This is a draft rule. Click Save to create it.
                  </AlertDescription>
                </Alert>
              )}

              {/* Alert for cash movement rules */}
              {rule.ruleType === "unexplained-cash-movement" && !isEditing && (
                <Alert className="border-orange-200 bg-orange-50">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    <strong>High-Risk Financial Compliance Rule:</strong> This rule detects unexplained cash movements 
                    which may indicate potential compliance issues or require additional documentation.
                  </AlertDescription>
                </Alert>
              )}

              {/* Basic Configuration */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Rule Name */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Rule Name
                    </Label>
                    {isEditing ? (
                      <Input
                        value={rule.ruleName || ''}
                        onChange={(e) => onUpdateRule(ruleId, { ruleName: e.target.value })}
                        placeholder="Enter rule name"
                        className="w-full"
                      />
                    ) : (
                      <div className="text-base font-medium">
                        {rule.ruleName || (isDraft ? 'New Rule (Draft)' : 'Unnamed Rule')}
                      </div>
                    )}
                  </div>

                  {/* Rule Type */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Rule Type * 
                    </Label>
                    {isEditing ? (
                      <Select
                        value={rule.ruleType || ''}
                        onValueChange={(value) => onUpdateRule(ruleId, { ruleType: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select rule type" />
                        </SelectTrigger>
                        <SelectContent>
                          {supportedRuleTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {rule.ruleType === "unexplained-cash-movement" ? "Unexplained Cash Movement" : (rule.ruleType || 'No type specified')}
                        </span>
                        {rule.ruleType === "unexplained-cash-movement" && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Financial Compliance
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Scope and Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Scope</Label>
                    {isEditing ? (
                      <Select
                        value={rule.scope || ''}
                        onValueChange={(value) => onUpdateRule(ruleId, { scope: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select scope" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fund">Fund</SelectItem>
                          <SelectItem value="account">Account</SelectItem>
                          <SelectItem value="transactions">Transaction</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {rule.scope || 'Not specified'}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Status</Label>
                    <div className="flex items-center gap-2">
                      {rule.disabled ? (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                          Disabled
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          Active
                        </Badge>
                      )}
                      {isDraft && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          Draft
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Accounts Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Target Accounts
                </Label>
                {isEditing ? (
                  <AccountSelector 
                    rule={rule} 
                    ruleId={ruleId} 
                    accounts={accounts}
                    accountsLoading={accountsLoading}
                    onUpdateRule={onUpdateRule}
                  />
                                ) : rule.ruleType === "Comparison" ? (
                  // Display comparison rule accounts (or initialization message)
                  isComparisonRule(rule.accounts) ? (
                    // Display comparison rule accounts
                    <div className="space-y-3">
                      {(() => {
                        const comparisonAccounts = parseComparisonAccounts(rule.accounts);
                        const aAccounts = getComparisonSelectedAccounts(comparisonAccounts.a, accounts);
                        const bAccounts = getComparisonSelectedAccounts(comparisonAccounts.b, accounts);
                        const excludeAccounts = getSelectedAccounts(rule.excludeAccounts, accounts);

                        return (
                          <>
                            {/* Account Set A */}
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-muted-foreground">Account Set A (Primary):</div>
                              <div className="flex flex-wrap gap-2">
                                {aAccounts.length > 0 ? (
                                  aAccounts.map((account) => (
                                    <Badge key={account.accountKey} variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                                      {account.accountKey} ({account.name})
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-sm text-muted-foreground italic">No accounts in Set A</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Account Set B */}
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-muted-foreground">Account Set B (Comparison):</div>
                              <div className="flex flex-wrap gap-2">
                                {bAccounts.length > 0 ? (
                                  bAccounts.map((account) => (
                                    <Badge key={account.accountKey} variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                                      {account.accountKey} ({account.name})
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-sm text-muted-foreground italic">All other accounts (no specific Set B)</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Exclude Accounts */}
                            {(excludeAccounts.length > 0) && (
                              <div className="space-y-1">
                                <div className="text-sm font-medium text-muted-foreground">Exclude Accounts:</div>
                                <div className="flex flex-wrap gap-2">
                                  {excludeAccounts.map((account) => (
                                    <Badge key={account.accountKey} variant="outline" className="text-xs bg-red-50 border-red-200 text-red-700">
                                      {account.accountKey} ({account.name})
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    // Show initialization message for comparison rules without proper structure
                    <div className="p-3 border border-orange-200 bg-orange-50 rounded-md">
                      <div className="text-sm text-orange-800">
                        <strong>Setting up comparison rule structure...</strong>
                      </div>
                      <div className="text-xs text-orange-600 mt-1">
                        This comparison rule will be configured with account sets A and B.
                      </div>
                    </div>
                  )
                ) : (
                  // Display regular include/exclude accounts
                  <div className="space-y-3">
                    {/* Include Accounts */}
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-muted-foreground">Include Accounts:</div>
                      <div className="flex flex-wrap gap-2">
                        {getSelectedAccounts(rule.accounts, accounts).length > 0 ? (
                          getSelectedAccounts(rule.accounts, accounts).map((account) => (
                            <Badge key={account.accountKey} variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700">
                              {account.accountKey} ({account.name})
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground italic">All accounts (no specific includes)</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Exclude Accounts */}
                    {(rule.excludeAccounts && getSelectedAccounts(rule.excludeAccounts, accounts).length > 0) && (
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-muted-foreground">Exclude Accounts:</div>
                        <div className="flex flex-wrap gap-2">
                          {getSelectedAccounts(rule.excludeAccounts, accounts).map((account) => (
                            <Badge key={account.accountKey} variant="outline" className="text-xs bg-red-50 border-red-200 text-red-700">
                              {account.accountKey} ({account.name})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Exception Rule */}
              <div className="space-y-2">
                {isEditing ? (
                  <JsonFieldsEditor
                    value={rule.exceptionRule}
                    onChange={(value) => onUpdateRule(ruleId, { exceptionRule: value })}
                    ruleId={ruleId}
                  />
                ) : rule.exceptionRule ? (
                  <div>
                    <Label className="text-sm font-medium">Exception Rule</Label>
                    <div className="mt-1 p-3 bg-muted rounded-md">
                      <pre className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {rule.exceptionRule}
                      </pre>
                    </div>
                  </div>
                ) : null}
              </div>

              <Separator />

              {/* Description Section */}
              <div className="space-y-4">
                <h4 className="font-medium">Descriptions</h4>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Short Description
                  </Label>
                  <div className="text-sm text-muted-foreground">
                    {rule.shortDescriptionTemplate || 'No description'}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Description Template
                  </Label>
                  <div className="text-sm text-muted-foreground max-w-2xl">
                    {rule.descriptionTemplate || 'No detailed description'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 min-w-[120px]">
              {isEditing ? (
                <>
                  <Button
                    onClick={() => onSaveRule(ruleId)}
                    disabled={isSaving || !rule.ruleName || !rule.ruleType}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isDraft ? 'Create' : 'Save'}
                  </Button>
                  <Button
                    onClick={() => onCancelEditing(ruleId)}
                    disabled={isSaving}
                    variant="outline"
                    className="w-full"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => onStartEditing(ruleId)}
                  variant="outline"
                  className="w-full"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 